import React, { useCallback, useEffect, useState } from "react";
import { Shuffle } from "lucide-react";
import { AppButton, AppModal, Callout, FormField, Textarea } from "../../ui";
import Shimmer from "../../Common/Shimmer";
import apiClient from "../../../utils/apiClient";
import { formatDay } from "./scheduleFormat";

const FORM_ID = "syllabus-repack-form";
const mins = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`);

/**
 * RepackDialog — re-form the classes still to be taught at the batch's class
 * length, after carry-overs left some too long (or a trainer got ahead).
 *
 * Only classes after the last one started or taught change. They keep their
 * dates; extra classes go on the next class days at the end; a class left
 * empty is removed (or skipped, when it holds a recording or a material).
 * The save is made against the preview shown — a plan changed meanwhile comes
 * back as a new preview.
 *
 * Props: show, onHide, batchId, onDone(message)
 *   overrun — { minutes, classMinutes } when it opens because a carry-over made a class run long
 */
const RepackDialog = ({ show, onHide, batchId, onDone, overrun }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setFailure("");
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/repack/preview`);
      setPreview(data);
    } catch (err) {
      setPreview(null);
      setFailure(err?.response?.data?.message || "The new plan could not be worked out.");
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (!show) return;
    setReason("");
    setNotice("");
    setSaving(false);
    load();
  }, [show, load]);

  const nothingToDo = preview && !preview.updates.length && !preview.creates.length && !preview.removes.length && !preview.skips.length;

  const submit = async (e) => {
    e.preventDefault();
    if (!preview || nothingToDo) return;
    setSaving(true);
    setFailure("");
    setNotice("");
    try {
      const { data } = await apiClient.post(`/syllabus/batch/${batchId}/syllabus/repack`, {
        fingerprint: preview.fingerprint,
        reason: reason.trim(),
      });
      onDone?.(data.message);
      onHide();
    } catch (err) {
      const body = err?.response?.data || {};
      if (body.errorCode === "SCHEDULE_CHANGED" && body.preview) {
        setPreview(body.preview);
        setNotice(body.message);
      } else {
        setFailure(body.message || "The plan was not re-packed. Try again.");
      }
      setSaving(false);
    }
  };

  return (
    <AppModal
      show={show}
      onHide={saving ? undefined : onHide}
      size="lg"
      title="Re-pack the classes still to teach"
      icon={<Shuffle size={18} aria-hidden="true" />}
      footer={(
        <div className="tw-flex tw-w-full tw-justify-end tw-gap-2">
          <AppButton variant="outline" onClick={onHide} disabled={saving}>Cancel</AppButton>
          <AppButton type="submit" form={FORM_ID} disabled={saving || loading || !preview || nothingToDo}>
            {saving ? "Re-packing…" : "Re-pack"}
          </AppButton>
        </div>
      )}
    >
      <form id={FORM_ID} onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          The topics still to teach are grouped again into classes of {preview ? mins(preview.classMinutes) : "the class length"}, in
          order. Classes keep their dates; nothing already taught changes.
        </p>
        {overrun && (
          <Callout tone="info">
            The class the topics went into now runs {mins(overrun.minutes)} — longer than its {mins(overrun.classMinutes)}.
          </Callout>
        )}
        {loading && <Shimmer height={56} count={3} />}
        {failure && <Callout tone="danger" role="alert">{failure}</Callout>}
        {notice && <Callout tone="warning">{notice}</Callout>}
        {preview && !loading && (nothingToDo ? (
          <Callout tone="success">Every class still to teach already fits its length.</Callout>
        ) : (
          <>
            <p className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content" aria-live="polite">
              {preview.before} class{preview.before === 1 ? "" : "es"} to teach become {preview.after}.
            </p>
            <ul className="tw-m-0 tw-flex tw-max-h-80 tw-list-none tw-flex-col tw-gap-1.5 tw-overflow-y-auto tw-rounded-md tw-border tw-border-solid tw-border-line tw-p-2">
              {preview.updates.map((u) => (
                <li key={`u${u.dayNumber}`} className="tw-flex tw-flex-col tw-rounded-sm tw-px-2 tw-py-1 tw-text-[0.8125rem]">
                  <span className="tw-text-content"><strong>Class {u.dayNumber}</strong> · {u.title} <span className="tw-text-content-muted">({mins(u.minutes)})</span></span>
                  <span className="tw-truncate tw-text-xs tw-text-content-muted">{u.topics.join(", ")}</span>
                </li>
              ))}
              {preview.creates.map((c) => (
                <li key={`c${c.dayNumber}`} className="tw-flex tw-flex-col tw-rounded-sm tw-bg-success/10 tw-px-2 tw-py-1 tw-text-[0.8125rem]">
                  <span className="tw-text-content"><strong>New class {c.dayNumber}</strong> on {formatDay(c.plannedDate)} · {c.title}</span>
                  <span className="tw-truncate tw-text-xs tw-text-content-muted">{c.topics.join(", ")}</span>
                </li>
              ))}
              {preview.removes.map((r) => (
                <li key={`r${r.dayNumber}`} className="tw-rounded-sm tw-bg-danger/10 tw-px-2 tw-py-1 tw-text-[0.8125rem] tw-text-content">
                  <strong>Class {r.dayNumber}</strong> is no longer needed and is removed.
                </li>
              ))}
              {preview.skips.map((r) => (
                <li key={`s${r.dayNumber}`} className="tw-rounded-sm tw-bg-warning/10 tw-px-2 tw-py-1 tw-text-[0.8125rem] tw-text-content">
                  <strong>Class {r.dayNumber}</strong> is left empty and marked skipped — it holds a recording or a linked material.
                </li>
              ))}
            </ul>
            {preview.unchanged > 0 && (
              <p className="tw-m-0 tw-text-xs tw-text-content-muted">{preview.unchanged} class{preview.unchanged === 1 ? "" : "es"} stay as they are.</p>
            )}
            <FormField label="Note (optional)" hint="Kept in the batch’s plan history.">
              <Textarea rows={2} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} />
            </FormField>
          </>
        ))}
      </form>
    </AppModal>
  );
};

export default RepackDialog;
